import { PaymentStatusDropdown } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Payments/components/PaymentStatusDropdown.tsx';
import { FooterSummaryCell } from '@dealstack/frontend/shared/components/Table/MantineTable/Cells/FooterSummaryCell.tsx';
import {
	CellContent,
	SingleLineText,
	singleLineTextStyle,
} from '@dealstack/frontend/shared/components/Table/MantineTable/Cells/cell.styles.ts';
import {
	formatPaymentAmount,
	getAccountDetailsCount,
	getBankAccountDetails,
	getInvestorName,
	getLatestActivityUniqueCount,
	getPaymentStatusColor,
	getPaymentStatusCount,
	PAYMENT_STATUS_SUMMARY_ORDER,
	PAYMENT_STATUSES,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Payments/components/Table.utils.tsx';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn.tsx';
import { createSortableTextHeader } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnHeader/ColumnHeader.tsx';
import { useMantineTableDataOperationsFromUrlParamState } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperationsFromUrlParamState.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import {
	DataMipPaymentRes,
	GetPaymentStatisticsRes,
	TransactionState,
} from '@dealstack/shared/lib/generated';
import { Box, Flex, Text } from '@mantine/core';
import { ColumnFiltersState } from '@tanstack/react-table';
import { useMemo } from 'react';
import { UpdatePaymentFn } from './Table.utils.types';
import {
	idListFilterFn,
	toIdListFilterValue,
} from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils.ts';

interface Props {
	payments: DataMipPaymentRes[];
	summary?: GetPaymentStatisticsRes;
	updatePayment: UpdatePaymentFn;
	onOpenIncompleteDrawer: (paymentIds: string[], annotation?: string | null) => void;
}

export const usePaymentsTable = ({
	payments,
	summary,
	updatePayment,
	onOpenIncompleteDrawer,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const mantineTableDataOperations = useMantineTableDataOperationsFromUrlParamState({
		initialPagination: { pageIndex: 0, pageSize: 10 },
	});

	const isFiltered =
		mantineTableDataOperations.columnFilters.some((filter) => hasFilterValue(filter.value)) ||
		!!mantineTableDataOperations.globalSearchString?.trim();

	const filteredPayments = useMemo(() => {
		const globalSearch = mantineTableDataOperations.globalSearchString?.trim().toLowerCase();
		const investorFilter = getStringColumnFilterValue(
			mantineTableDataOperations.columnFilters,
			'investor'
		);
		const paymentStatusFilter = getListColumnFilterValue(
			mantineTableDataOperations.columnFilters,
			'paymentStatus'
		);
		const idFilter = getStringColumnFilterValue(
			mantineTableDataOperations.columnFilters,
			'investmentId'
		);
		const investorIdFilter = getListColumnFilterValue(
			mantineTableDataOperations.columnFilters,
			'investorId'
		);

		return payments.filter((payment) => {
			const investorName = getInvestorName(payment).toLowerCase();

			if (investorFilter && !investorName.includes(investorFilter.toLowerCase())) {
				return false;
			}

			if (investorIdFilter.length && !investorIdFilter.includes(payment.investorId)) {
				return false;
			}

			if (
				paymentStatusFilter.length &&
				!paymentStatusFilter.includes(payment.paymentStatus)
			) {
				return false;
			}

			if (idFilter && !payment.id.toLowerCase().includes(idFilter.toLowerCase())) {
				return false;
			}

			if (globalSearch && !investorName.includes(globalSearch)) {
				return false;
			}

			return true;
		});
	}, [
		mantineTableDataOperations.columnFilters,
		mantineTableDataOperations.globalSearchString,
		payments,
	]);

	const columns = useMemo(
		() => [
			createTextColumn<DataMipPaymentRes>({
				id: 'investmentId',
				headerText: tMipManage('payments.table.investment_col_id'),
				columnLabel: tMipManage('payments.table.investment_col_id'),
				accessorFn: (row) => row.id,
				enableSorting: false,
				enableGlobalFilter: false,
				showFilter: true,
				filterText: tMipManage('payments.table.investment_col_id.filter'),
				filterVariant: 'text',
				cell: ({ row }) => (
					<CellContent>
						<Text
							size="sm"
							truncate
							component={SingleLineText}
							style={singleLineTextStyle}
						>
							{row.original.id}
						</Text>
					</CellContent>
				),
			}),
			createTextColumn<DataMipPaymentRes>({
				id: 'investorId',
				headerText: tMipManage('payments.table.investor_id'),
				columnLabel: tMipManage('payments.table.investor_id'),
				accessorFn: (row) => row.investorId,
				filterFn: idListFilterFn,
				enableSorting: false,
				enableGlobalFilter: false,
				showFilter: true,
				filterText: tMipManage('payments.table.investor_id'),
				filterVariant: 'text',
			}),
			createTextColumn<DataMipPaymentRes>({
				id: 'investor',
				headerText: tMipManage('payments.table.investor'),
				columnLabel: tMipManage('payments.table.investor'),
				accessorFn: (row) => getInvestorName(row),
				minSize: 220,
				meta: { widthPercentage: 33 },
				enableSorting: true,
				enableGlobalFilter: true,
				showFilter: true,
				filterText: 'Filter investor',
				filterVariant: 'text',
				cell: ({ row }) => (
					<CellContent>
						<Text
							size="sm"
							fw={500}
							truncate
							component={SingleLineText}
							style={singleLineTextStyle}
						>
							{getInvestorName(row.original)}
						</Text>
						<Text
							size="sm"
							c="dark.0"
							truncate
							component={SingleLineText}
							style={singleLineTextStyle}
						>
							{row.original.investorEmail || LONG_DASH}
						</Text>
					</CellContent>
				),
				showFooter: true,
				footer: () => (
					<FooterSummaryCell
						label={tMipManage('payments.footer.number_of_records')}
						value={
							isFiltered
								? filteredPayments.length
								: summary?.paymentCount ?? payments.length
						}
					/>
				),
			}),
			createTextColumn<DataMipPaymentRes>({
				id: 'accountDetails',
				headerText: tMipManage('payments.table.account_details'),
				columnLabel: tMipManage('payments.table.account_details'),
				accessorFn: (row) => getBankAccountDetails(row).line1,
				minSize: 220,
				meta: { widthPercentage: 31 },
				enableSorting: false,
				enableGlobalFilter: false,
				cell: ({ row }) => {
					const accountDetails = getBankAccountDetails(row.original);

					return (
						<CellContent>
							<Text
								size="sm"
								truncate
								component={SingleLineText}
								style={singleLineTextStyle}
							>
								{accountDetails.line1}
							</Text>
							{accountDetails.detailLines.map((line) => (
								<Text
									key={line}
									size="sm"
									c="dark.0"
									truncate
									component={SingleLineText}
								>
									{line}
								</Text>
							))}
						</CellContent>
					);
				},
				showFooter: true,
				footer: () => (
					<FooterSummaryCell
						label={tMipManage('payments.footer.number_of_records')}
						value={getAccountDetailsCount(filteredPayments)}
					/>
				),
			}),
			createTextColumn<DataMipPaymentRes>({
				id: 'amount',
				header: createSortableTextHeader(
					'Amount',
					true,
					{ style: { marginLeft: 'auto' } },
					'left'
				),
				columnLabel: 'Amount',
				accessorFn: (row) => row.totalCost,
				minSize: 120,
				meta: { widthPercentage: 12 },
				enableSorting: true,
				enableGlobalFilter: false,
				cell: ({ row }) => {
					return (
						<Flex w="100%" justify="flex-end">
							<Text size="sm" c={row.original.isNegative ? 'red.8' : undefined}>
								{formatPaymentAmount(row.original.totalCost, row.original.currency)}
							</Text>
						</Flex>
					);
				},
				showFooter: true,
				footer: () => (
					<FooterSummaryCell
						label={tMipManage('payments.footer.total_amount')}
						value={formatPaymentAmount(
							filteredPayments.reduce(
								(acc, payment) => acc + (payment.totalCost || 0),
								0
							),
							filteredPayments[0]?.currency
						)}
						align="flex-end"
					/>
				),
			}),
			createTextColumn<DataMipPaymentRes>({
				id: 'paymentStatus',
				headerText: tMipManage('payments.table.payment_status'),
				columnLabel: tMipManage('payments.table.payment_status'),
				accessorFn: (row) => row.paymentStatus,
				enableSorting: true,
				enableGlobalFilter: false,
				minSize: 140,
				meta: { widthPercentage: 14 },
				showFilter: true,
				filterText: 'Filter status',
				filterVariant: 'multi-select',
				mantineFilterMultiSelectProps: {
					data: PAYMENT_STATUSES.map((status) => ({
						value: status,
						label: tMipManage(getPaymentStatusTranslationKey(status)),
					})),
				},
				filterFn: (row, columnId, filterValue) =>
					!Array.isArray(filterValue) ||
					filterValue.length === 0 ||
					filterValue.includes(row.getValue(columnId)),
				cell: ({ row }) => (
					<PaymentStatusDropdown
						payment={row.original}
						updatePayment={updatePayment}
						onOpenIncompleteDrawer={onOpenIncompleteDrawer}
					/>
				),
				showFooter: true,
				footer: () => (
					<Flex h="100%" w="100%" align="center">
						<Flex align="center" gap={8}>
							{PAYMENT_STATUS_SUMMARY_ORDER.map((status) => (
								<Flex key={status} align="center" gap={4}>
									<Box
										w={6}
										h={6}
										style={{
											borderRadius: '50%',
											backgroundColor: getPaymentStatusColor(status),
										}}
									/>
									<Text size="sm" fw={600}>
										{isFiltered
											? getPaymentStatusCount(filteredPayments, status)
											: getPaymentStatusCount(
													payments,
													status,
													summary?.byStatus
												)}
									</Text>
								</Flex>
							))}
						</Flex>
					</Flex>
				),
			}),
			createTextColumn<DataMipPaymentRes>({
				id: 'latestActivity',
				headerText: tMipManage('payments.table.latest_activity'),
				columnLabel: tMipManage('payments.table.latest_activity'),
				accessorFn: (row) => row.updatedAt,
				enableSorting: true,
				enableGlobalFilter: false,
				minSize: 120,
				meta: { widthPercentage: 10 },
				cell: ({ row }) => (
					<Text size="sm">
						{row.original.updatedAt
							? formatStringDateToUI(row.original.updatedAt)
							: LONG_DASH}
					</Text>
				),
				showFooter: true,
				footer: () => (
					<FooterSummaryCell
						label={tMipManage('payments.footer.unique_count')}
						value={getLatestActivityUniqueCount(filteredPayments)}
					/>
				),
			}),
		],
		[
			filteredPayments,
			isFiltered,
			onOpenIncompleteDrawer,
			payments,
			summary,
			tMipManage,
			updatePayment,
		]
	);

	return {
		columns,
		mantineTableDataOperations,
	};
};

const getListColumnFilterValue = (columnFilters: ColumnFiltersState, columnId: string): string[] =>
	toIdListFilterValue(columnFilters.find((filter) => filter.id === columnId)?.value);

const getStringColumnFilterValue = (columnFilters: ColumnFiltersState, columnId: string) => {
	const filterValue = columnFilters.find((filter) => filter.id === columnId)?.value;

	return typeof filterValue === 'string' ? filterValue.trim() : undefined;
};

const hasFilterValue = (filterValue: unknown) => {
	if (Array.isArray(filterValue)) {
		return filterValue.some((value) => hasFilterValue(value));
	}

	if (typeof filterValue === 'string') {
		return filterValue.trim().length > 0;
	}

	return filterValue != null;
};

const getPaymentStatusTranslationKey = (status: TransactionState) => {
	switch (status) {
		case TransactionState.COMPLETED:
			return 'payment_status.completed';
		case TransactionState.INCOMPLETE:
			return 'payment_status.incomplete';
		case TransactionState.PENDING:
			return 'payment_status.pending';
		default:
			return 'payment_status.pending';
	}
};
